import * as React from 'react'
import { ICheckAccessProps, IPermissionContextModel } from 'Models/PermissionContextModel'
import PermissionContext from 'Contexts/PermissionContext'
import userManager from 'Oidc/userManager'
import { resetConfigs } from 'Services/httpConfig'
import { instance } from 'Services/instance'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IApplicationState } from 'Store/types'
import AccessDeniedPage from './AccessDeniedPage'
import useInitializer from 'Hooks/useInitializer'
import { SigninResponse, User } from 'oidc-client-ts'
import Const from 'Utils/Consts'
import QueryStringBuilder from 'Utils/QueryStringBuilder.Utilts'
import { useSettings } from 'Contexts/SettingsContext'
import { useGetAllAccessListQuery } from 'Services/api'

interface IProps {
  children?: any
}

const EmptyLayout = (props: IProps) => {
  const { user } = useSelector((rootState: IApplicationState) => rootState.oidc)
  const { initialize, initializerLoading, initializerLoaded } = useInitializer()
  const [getToken, setGetToken] = useState<boolean>(false)
  const { settings } = useSettings()

  const { data: userAccessAndRoles } = useGetAllAccessListQuery(undefined, {
    skip: !initializerLoaded || initializerLoading,
  })

  const [contextValue, setContextValue] = React.useState<IPermissionContextModel>({
    token: user?.access_token || '',
    isAuth: !!user?.access_token,
    loading: true,
    setToken,
    logOut,
    GetClaimValue,
    HasClaim,
    HasAccess,
    HasAnyAccess,
    HasAllAccess,
    HasRole,
    HasAnyRole,
    CheckAccessIf,
    CheckAccess,
  })

  useEffect(() => {
    setContextValue(prev => ({
      ...prev,
      loading: initializerLoading || !initializerLoaded,
      loaded: initializerLoaded,
    }))
  }, [initializerLoading, initializerLoaded])

  useEffect(() => {
    userManager.getUser().then(user => {
      if (user && !user.expired) {
        addTokenInterceptor(user?.access_token)
        setToken(user.access_token)
        initialize()
      } else {
        setContextValue(prev => ({ ...prev, loading: false, isAuth: false }))
      }
    })

    userManager.events.addAccessTokenExpiring(() => {
      userManager.getUser().then(async (user: User | null) => {
        if (!getToken) {
          setGetToken(true)
          const state = user as Required<User>
          await refreshToken(state)
        }
      })
    })
  }, [])

  const refreshToken = async (state: any): Promise<User> => {
    const result = await instance.post(
      `${Const.env.REACT_APP_API_GATEWAY_URL}digital_software_management/api/AuthenticationPod/RefreshToken`,
      QueryStringBuilder({
        clientId: Const.env.REACT_APP_CLIENT_ID,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    )

    if (result.status == 400) {
      throw new Error('Error_On_Refresh_Token')
    }

    const response = new SigninResponse(new URLSearchParams())
    Object.assign(response, result.data)

    const temp = { ...state, ...response }
    temp.profile = state.profile

    const user = new User(temp)
    await storeUser(user)
    resetConfigs()
    addTokenInterceptor(user.access_token)

    setGetToken(false)
    return user
  }

  const storeUser = async (user: User | null) => {
    const userStoreKey = `user:${userManager.settings.authority}:${userManager.settings.client_id}`
    if (user) {
      const storageString = user.toStorageString()
      await userManager.settings.userStore.set(userStoreKey, storageString)
    } else {
      await userManager.settings.userStore.remove(userStoreKey)
    }
  }

  useEffect(() => {
    if (user && user.access_token) {
      setToken(user.access_token)
    } else {
      setToken('')
    }
  }, [user])

  function setToken(token: string, onLogin?: () => void) {
    if (token != '') {
      setTimeout(() => {
        let newContextValue = { ...contextValue }
        // let tokenData: any = jwt_decode(token);
        newContextValue.token = token
        newContextValue.isAuth = token != ''
        newContextValue.logOut = logOut
        newContextValue.GetClaimValue = GetClaimValue
        newContextValue.HasClaim = HasClaim
        newContextValue.HasAccess = HasAccess
        newContextValue.HasAllAccess = HasAllAccess
        newContextValue.HasAnyAccess = HasAnyAccess
        newContextValue.HasRole = HasRole
        newContextValue.HasAnyRole = HasAnyRole
        newContextValue.CheckAccessIf = CheckAccessIf
        newContextValue.CheckAccess = CheckAccess
        newContextValue.loading = initializerLoading
        newContextValue.loaded = initializerLoaded
        setContextValue(newContextValue)
        if (!initializerLoaded && !initializerLoading) {
          addTokenInterceptor(token)
          initialize()
        }

        if (onLogin) onLogin()
      }, 10)
    } else {
      let newContextValue = { ...contextValue }
      newContextValue.isAuth = false
      newContextValue.logOut = logOut
      newContextValue.GetClaimValue = GetClaimValue
      newContextValue.HasClaim = HasClaim
      newContextValue.HasAccess = HasAccess
      newContextValue.HasAllAccess = HasAllAccess
      newContextValue.HasAnyAccess = HasAnyAccess
      newContextValue.HasRole = HasRole
      newContextValue.HasAnyRole = HasAnyRole
      newContextValue.CheckAccessIf = CheckAccessIf
      newContextValue.CheckAccess = CheckAccess
      newContextValue.loading = initializerLoading
      newContextValue.loaded = initializerLoaded
      setContextValue(newContextValue)
    }
  }

  function addTokenInterceptor(token: any) {
    instance.interceptors.request.use((request: any) => {
      request.headers.common.Authorization = `${Const.env.REACT_APP_REACT_FAKE_USER == false ? 'Bearer' : 'Fake'} ${token}`
      // request.headers.common.Authorization = `Bearer ${token}`
      return request
    })
  }

  function logOut() {
    setContextValue(prev => ({
      ...prev,
      isAuth: false,
      token: '',
    }))
  }

  function GetClaimValue(claim: string): any {
    return user?.profile[claim]
  }

  function HasClaim(claim: string): boolean {
    return false
  }

  function HasAccess(access: string): boolean {
    if (access) {
      return (
        !!userAccessAndRoles?.permissions &&
        userAccessAndRoles?.permissions.some(userAccess => userAccess.name == access)
      )
    } else return true
  }

  function HasAnyAccess(accesses: any[]): boolean {
    return accesses.length == 0 || accesses.some(x => HasAccess(x) === true)
  }

  function HasAllAccess(accesses: any[]): boolean {
    return accesses.length == 0 || !accesses.some(x => HasAccess(x) === false)
  }

  function HasRole(role: string): boolean {
    return (
      !!userAccessAndRoles?.roles && userAccessAndRoles?.roles.some(userRole => userRole == role)
    )
  }

  function HasAnyRole(roles: string[]): boolean {
    return roles.some(x => HasRole(x) === true)
  }

  function CheckAccessIf(accsess: any): boolean {
    if (Array.isArray(accsess)) {
      return HasAnyAccess(accsess)
    } else {
      return HasAccess(accsess)
    }
  }

  function CheckAccess(props: ICheckAccessProps): any {
    if (Array.isArray(props.accsess)) {
      return HasAnyAccess(props.accsess) ? (
        props.children
      ) : props.withAccessDeniedPage ? (
        <AccessDeniedPage />
      ) : null
    } else
      return HasAccess(props.accsess) ? (
        props.children
      ) : props.withAccessDeniedPage ? (
        <AccessDeniedPage />
      ) : null
  }

  return (
    <PermissionContext.Provider value={contextValue}>{props.children}</PermissionContext.Provider>
  )
}

export default EmptyLayout
